import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Input, Col, Row, message, Select, Spin } from 'antd';
import countriesJson from '../../data/countries.json';
import {
    fetchUserProfile,
    updateUserProfileThunk,
    changePasswordThunk,
    resetChangePasswordStatus
} from '../../redux/slices/profileSlice';

const Profile = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const { data, loading, error, changePasswordStatus } = useSelector((state) => state.profile);

    const countries = useMemo(
        () =>
            countriesJson?.map((country) => ({
                value: country.name?.common,
                label: country.name?.common,
            })),
        []
    );

    useEffect(() => {
        dispatch(fetchUserProfile());
    }, [dispatch]);

    useEffect(() => {
        if (data) {
            form.setFieldsValue({ user: data });
        }
    }, [data, form]);

    const onFinish = async (values) => {
        try {
            const emailChanged = values?.user?.email !== data?.email;
            // PT-04: the email only changes after the new address is verified; the current
            // password is required for that request.
            const payload = { ...values.user, currentPassword: emailChanged ? values.currentPassword : undefined };
            await dispatch(updateUserProfileThunk(payload)).unwrap();
            message.success(
                emailChanged
                    ? `Verification link sent to ${values.user.email}. Your email will change once you confirm it.`
                    : 'Profile updated successfully!',
                emailChanged ? 8 : 3
            );
            form.setFieldsValue({ currentPassword: undefined });
            dispatch(fetchUserProfile()); // Re-fetch updated profile
        } catch (error) {
            message.error(error || 'Failed to update profile.');
        }
    };

    const handleChangePassword = async () => {
        try {
            await dispatch(changePasswordThunk(data.email)).unwrap();
            message.success('Password reset link sent successfully!');
        } catch (error) {
            message.error(error || 'Failed to send password reset link.');
        }finally {
            // Clear the password status after 5 seconds
            setTimeout(() => {
                dispatch(resetChangePasswordStatus());
            }, 5000);
        }
    };

    return (
        <Col span={10} offset={1}>
            {loading ? (
                <Spin size="large"/>
            ) : error ? (
                <p>Error: {error}</p>
            ) : (
                <div className="wrapper">
                    <h2>User Profile</h2>
                    <Form
                        form={form}
                        layout="vertical"
                        name="profileForm"
                        onFinish={onFinish}
                        validateMessages={{
                            required: '${label} is required!',
                            types: { email: '${label} is not a valid email!' },
                        }}
                    >
                        <Form.Item
                            name={['user', 'firstName']}
                            label="First Name"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="First name" />
                        </Form.Item>
                        <Form.Item
                            name={['user', 'lastName']}
                            label="Last Name"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Last name" />
                        </Form.Item>
                        <Form.Item
                            name={['user', 'userName']}
                            label="Username"
                        >
                            <Input placeholder="Username" readOnly />
                        </Form.Item>
                        <Form.Item
                            name={['user', 'email']}
                            label="Email"
                            rules={[{ type: 'email' }]}
                        >
                            <Input placeholder="Email" />
                        </Form.Item>
                        {data?.pendingEmail && (
                            <Form.Item label=" " colon={false}>
                                <span style={{ color: '#888' }}>
                                    Pending verification: {data.pendingEmail} (check that inbox for the confirmation link)
                                </span>
                            </Form.Item>
                        )}
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.user?.email !== cur?.user?.email}>
                            {({ getFieldValue }) =>
                                getFieldValue(['user', 'email']) !== data?.email && (
                                    <Form.Item
                                        name="currentPassword"
                                        label="Current Password"
                                        rules={[{ required: true, message: 'Enter your current password to change the email' }]}
                                        extra="Required to change your email address. A verification link will be sent to the new address."
                                    >
                                        <Input.Password placeholder="Current password" autoComplete="current-password" />
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item
                            name={['user', 'country']}
                            label="Country"
                            rules={[{ required: true }]}
                        >
                            <Select
                                size="middle"
                                showSearch
                                placeholder="Select User's Country"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={countries}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Row>
                                <Col>
                                    <Button loading={loading} type="primary" htmlType="submit">
                                        Submit
                                    </Button>
                                </Col>
                                <Col offset={1}>
                                    <Button
                                        loading={changePasswordStatus?.loading}
                                        type="secondary"
                                        onClick={handleChangePassword}
                                    >
                                        Change Password
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Item>
                    </Form>
                    {changePasswordStatus?.message && (
                        <Row
                            className={
                                changePasswordStatus.success
                                    ? 'success-message'
                                    : 'error-message'
                            }
                        >
                            {changePasswordStatus.message}
                        </Row>
                    )}
                </div>
            )}
        </Col>
    );
};

export default Profile;

